import { sortByName, sortAsc } from '../utils/sortOptions';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  PlusIcon, PencilSquareIcon, XMarkIcon,
  ChartBarIcon, LinkIcon, CheckCircleIcon,
} from '@heroicons/react/24/outline';

const DOMAINS    = ['cognitive','affective','psychomotor'];
const DOMAIN_COLOR = { cognitive:'#3b6ef8', affective:'#10c98e', psychomotor:'#f59e0b' };
const curYear = () => { const y=new Date().getFullYear(); return `${y}-${y+1}`; };

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .pm * { box-sizing:border-box; }
  .pm { font-family:'Sora',sans-serif; color:#1a1d2e; animation:pmFade .4s ease both; }
  @keyframes pmFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .pm-hdr { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .pm-title { font-size:1.35rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; }
  .pm-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .tab-row { display:flex; gap:4px; background:#f0f2f7; border-radius:11px; padding:4px; margin-bottom:1.4rem; width:fit-content; flex-wrap:wrap; }
  .tab-btn { padding:7px 18px; border-radius:8px; border:none; font-family:'Sora',sans-serif; font-size:.78rem; font-weight:600; cursor:pointer; transition:all .15s; background:transparent; color:#9094a8; }
  .tab-btn.active { background:white; color:#0f1123; box-shadow:0 1px 4px rgba(0,0,0,.1); }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:8px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-blue  { background:#3b6ef8; color:white; } .btn-blue:hover  { background:#2d5ef0; }
  .btn-ghost { background:#f0f2f7; color:#6b7194; } .btn-ghost:hover { background:#e4e7f0; }
  .btn-red   { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; } .btn-red:hover { background:#ef4444; color:white; }
  .btn-green { background:#10c98e; color:white; } .btn-green:hover { background:#0db87e; }
  .btn-sm { padding:5px 10px; font-size:.73rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1rem; }
  .panel-head { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .plo-table { width:100%; border-collapse:collapse; }
  .plo-table thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .plo-table th { padding:10px 14px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .plo-table tbody tr { border-bottom:1px solid #f3f5fb; transition:background .12s; }
  .plo-table tbody tr:last-child { border-bottom:none; }
  .plo-table tbody tr:hover { background:#fafbff; }
  .plo-table td { padding:11px 14px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .plo-code { font-family:'JetBrains Mono',monospace; font-size:.78rem; font-weight:700; color:#8b5cf6; background:#f3f0ff; padding:3px 9px; border-radius:6px; display:inline-block; }
  .pill { display:inline-flex; font-size:.66rem; font-weight:600; padding:2px 9px; border-radius:99px; white-space:nowrap; text-transform:capitalize; }
  .clo-count-badge { display:inline-flex; align-items:center; gap:4px; font-size:.72rem; font-weight:600; padding:3px 10px; border-radius:99px; background:#eef2ff; color:#3b6ef8; }
  .pm-empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .pm-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#8b5cf6; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg)} }
  /* Map CLOs tab */
  .map-filters { display:flex; gap:10px; margin-bottom:1.2rem; flex-wrap:wrap; align-items:flex-end; }
  .fl { display:flex; flex-direction:column; gap:4px; }
  .fl-label { font-size:.67rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .fl-select,.fl-input { font-family:'Sora',sans-serif; font-size:.8rem; padding:8px 12px; border:1.5px solid #e0e4f0; border-radius:9px; background:white; outline:none; min-width:130px; }
  .fl-select:focus,.fl-input:focus { border-color:#3b6ef8; }
  .clo-map-row { display:flex; align-items:center; justify-content:space-between; padding:11px 14px; border-bottom:1px solid #f3f5fb; gap:10px; flex-wrap:wrap; }
  .clo-map-row:last-child { border-bottom:none; }
  .clo-map-info { flex:1; min-width:0; }
  .clo-map-code { font-family:'JetBrains Mono',monospace; font-size:.76rem; font-weight:700; color:#3b6ef8; }
  .clo-map-desc { font-size:.76rem; color:#1a1d2e; margin-top:2px; }
  .clo-map-meta { font-size:.68rem; color:#9094a8; margin-top:2px; }
  /* Modal */
  .pm-modal-backdrop { position:fixed; top:0; left:0; width:100vw; height:100vh; background:rgba(15,17,35,.65); backdrop-filter:blur(6px); display:flex; align-items:center; justify-content:center; z-index:99999; overflow:hidden; }
  .pm-modal { background:white; border-radius:20px; padding:26px; width:520px; max-width:calc(100vw - 40px); box-shadow:0 24px 64px rgba(0,0,0,.3); animation:moIn .25s ease; }
  @keyframes moIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .modal-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:18px; }
  .modal-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .modal-close { width:30px; height:30px; border-radius:8px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .modal-close svg { width:14px; height:14px; stroke-width:2; }
  .modal-grid { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
  .mf { display:flex; flex-direction:column; gap:5px; }
  .mf.full { grid-column:1/-1; }
  .mf-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .mf-input,.mf-select,.mf-textarea { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .mf-input:focus,.mf-select:focus,.mf-textarea:focus { border-color:#8b5cf6; background:white; }
  .mf-textarea { resize:vertical; min-height:52px; }
  .mf-hint { font-size:.67rem; color:#b0b5cc; }
  .modal-btns { display:flex; gap:8px; justify-content:flex-end; margin-top:18px; }
  /* Mobile cards for PLO table */
  .pm-cards { display:none; padding:10px; }
  .pm-mcard { background:white; border-radius:12px; border:1px solid #e8ebf4; padding:13px; margin-bottom:8px; }
  .pm-mc-top { display:flex; align-items:center; justify-content:space-between; gap:8px; flex-wrap:wrap; margin-bottom:8px; }
  .pm-mc-desc { font-size:.78rem; color:#1a1d2e; line-height:1.45; margin-bottom:10px; }
  .pm-mc-row { display:flex; justify-content:space-between; align-items:center; font-size:.76rem; padding:5px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .pm-mc-row:last-of-type { border-bottom:none; }
  .pm-mc-label { color:#9094a8; font-weight:600; white-space:nowrap; }
  .pm-mc-actions { display:flex; gap:6px; margin-top:10px; }
  @media (max-width:600px) { .pm-title{font-size:1.1rem;} .modal-grid{grid-template-columns:1fr;} .mf.full{grid-column:1;} .pm-modal{padding:18px;} .map-filters{flex-direction:column;} .fl-select,.fl-input{width:100%;min-width:unset;} }
  @media (max-width:768px) {
    .pm-tbl-wrap { display:none !important; }
    .pm-cards { display:block; }
  }
`;

export default function PLOManagement({ readOnly=false }) {
  const [tab,         setTab]        = useState('plos');
  const [plos,        setPlos]       = useState([]);
  const [clos,        setClos]       = useState([]);
  const [classes,     setClasses]    = useState([]);
  const [loading,     setLoading]    = useState(false);
  const [cloLoading,  setCloLoading] = useState(false);
  const [modal,       setModal]      = useState(null); // null | 'add' | plo obj
  const [form,        setForm]       = useState({ code:'', description:'', domain:'cognitive', threshold:'' });
  const [saving,      setSaving]     = useState(false);
  const [savingMap,   setSavingMap]  = useState(false);
  // CLO map filters
  const [mapClass,    setMapClass]   = useState('');
  const [mapSubject,  setMapSubject] = useState('');
  const [mapYear,     setMapYear]    = useState(curYear());
  // Local CLO→PLO mapping state (ploId per cloId)
  const [cloMappings, setCloMappings]= useState({}); // { cloId: ploId | '' }

  useEffect(() => {
    fetchPLOs();
    api.get('/admin/classes').then(r => setClasses(r.data?.classes||r.data||[])).catch(()=>{});
  }, []);

  useEffect(() => { if (tab === 'map') fetchCLOs(); }, [tab, mapClass, mapSubject, mapYear]);

  useEffect(() => {
    if (modal) { document.documentElement.style.overflow='hidden'; document.body.style.overflow='hidden'; }
    else       { document.documentElement.style.overflow=''; document.body.style.overflow=''; }
    return () => { document.documentElement.style.overflow=''; document.body.style.overflow=''; };
  }, [modal]);

  const fetchPLOs = async () => {
    setLoading(true);
    try { const { data } = await api.get(readOnly ? '/coordinator/plos' : '/admin/plos'); setPlos(data); }
    catch { toast.error('Failed to load PLOs'); }
    finally { setLoading(false); }
  };

  const fetchCLOs = async () => {
    setCloLoading(true);
    try {
      const p = new URLSearchParams();
      if (mapClass)   p.append('classId',     mapClass);
      if (mapSubject) p.append('subject',      mapSubject);
      if (mapYear)    p.append('academicYear', mapYear);
      const { data } = await api.get(`/admin/clos?${p}`);
      const list = Array.isArray(data) ? data : [];
      setClos(list);
      // Build initial mapping state from CLO.plo field
      const m = {};
      list.forEach(c => { m[c._id] = c.plo?._id || c.plo || ''; });
      setCloMappings(m);
    } catch { toast.error('Failed to load CLOs'); }
    finally { setCloLoading(false); }
  };

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const openAdd = () => {
    setForm({ code:'', description:'', domain:'cognitive', threshold:'' });
    setModal('add');
  };

  const openEdit = (plo) => {
    setForm({ code: plo.code, description: plo.description, domain: plo.domain, threshold: plo.threshold ?? '' });
    setModal(plo);
  };

  const handleSave = async () => {
    if (!form.code || !form.description) return toast.error('Code and description are required');
    setSaving(true);
    try {
      if (readOnly && modal === 'edit') {
        // Coordinator edit request
        await api.post('/coordinator/request/plo-edit', {
          ploId: modal._id, ploCode: modal.code,
          description: form.description, domain: form.domain,
          threshold: form.threshold || null,
        });
        toast.success('PLO edit request submitted');
        setModal(null);
      } else if (readOnly && modal === 'add') {
        // Coordinator create request
        await api.post('/coordinator/request/plo-create', {
          code: form.code, description: form.description,
          domain: form.domain, threshold: form.threshold || null,
        });
        toast.success('PLO creation request submitted');
        setModal(null);
      } else if (modal === 'add') {
        await api.post('/admin/plos', { ...form, threshold: form.threshold || null });
        toast.success('PLO created');
        setModal(null);
        fetchPLOs();
      } else {
        await api.put(`/admin/plos/${modal._id}`, { description: form.description, domain: form.domain, threshold: form.threshold || null });
        toast.success('PLO updated');
        setModal(null);
        fetchPLOs();
      }
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleSaveMappings = async () => {
    setSavingMap(true);
    try {
      const mappings = Object.entries(cloMappings).map(([cloId, ploId]) => ({ cloId, ploId: ploId || null }));
      if (readOnly) {
        await api.post('/coordinator/request/plo-map-clos', { mappings });
        toast.success('CLO → PLO mapping request submitted');
      } else {
        await api.put('/admin/plos/map-clos', { mappings });
        toast.success('CLO → PLO mappings saved');
        fetchPLOs();
      }
    } catch { toast.error('Failed'); }
    finally { setSavingMap(false); }
  };

  const classSubjects = (classId) => {
    const cls = classes.find(c => c._id === classId);
    if (!cls?.subjects) return [];
    return [...new Set((Array.isArray(cls.subjects)?cls.subjects:[cls.subjects]).flatMap(s=>String(s).split(',').map(x=>x.trim())).filter(Boolean))].sort();
  };

  const mapSubjects = mapClass ? classSubjects(mapClass) : [...new Set(clos.map(c=>c.subject).filter(Boolean))].sort();

  return (
    <div className="pm"><style>{css}</style>

      <div className="pm-hdr">
        <div>
          <div className="pm-title">OBE — PLO Management</div>
          <div className="pm-sub">Define Program Learning Outcomes and map CLOs to them</div>
        </div>
        {tab==='plos' && !readOnly && <button className="btn btn-blue" onClick={openAdd}><PlusIcon />Add PLO</button>}
        {tab==='plos' &&  readOnly && <button className="btn btn-ghost" onClick={()=>setModal('add')}><PlusIcon />Request PLO</button>}
        {tab==='map'  && <button className="btn btn-green" onClick={handleSaveMappings} disabled={savingMap}><CheckCircleIcon />{savingMap?'Saving…':'Save Mappings'}</button>}
      </div>

      <div className="tab-row">
        <button className={`tab-btn ${tab==='plos'?'active':''}`} onClick={()=>setTab('plos')}>PLOs</button>
        <button className={`tab-btn ${tab==='map'?'active':''}`}  onClick={()=>setTab('map')}><LinkIcon style={{width:12,height:12,display:'inline',marginRight:4}}/>Map CLOs → PLOs</button>
      </div>

      {/* ── PLOs List ── */}
      {tab==='plos' && (
        <div className="panel">
          <div className="panel-head">
            <div className="panel-title">Program Learning Outcomes ({plos.length})</div>
          </div>
          {loading ? (
            <div className="pm-loading"><div className="spinner"/>Loading…</div>
          ) : plos.length === 0 ? (
            <div className="pm-empty">
              No PLOs defined yet.{' '}
              <button onClick={openAdd} style={{color:'#8b5cf6',background:'none',border:'none',cursor:'pointer',fontWeight:600,fontFamily:'Sora',fontSize:'.82rem'}}>
                Add first PLO →
              </button>
            </div>
          ) : (
            <>
              <div className="pm-tbl-wrap" style={{overflowX:'auto'}}>
                <table className="plo-table">
                  <thead><tr>
                    <th>Code</th><th>Description</th><th>Domain</th>
                    <th>Threshold</th><th>CLOs Mapped</th><th>Actions</th>
                  </tr></thead>
                  <tbody>
                    {plos.map(plo => (
                      <tr key={plo._id}>
                        <td><span className="plo-code">{plo.code}</span></td>
                        <td style={{maxWidth:280,fontSize:'.78rem'}}>{plo.description}</td>
                        <td><span className="pill" style={{background:`${DOMAIN_COLOR[plo.domain]}18`,color:DOMAIN_COLOR[plo.domain]}}>{plo.domain}</span></td>
                        <td style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.78rem',fontWeight:600,color:plo.threshold!=null?'#8b5cf6':'#c0c5dc'}}>
                          {plo.threshold!=null?`${plo.threshold}%`:'default'}
                        </td>
                        <td>
                          <span className="clo-count-badge">
                            <ChartBarIcon style={{width:12,height:12,strokeWidth:2}}/>
                            {plo.cloCount} CLO{plo.cloCount!==1?'s':''}
                          </span>
                        </td>
                        <td>
                          <div style={{display:'flex',gap:5}}>
                            <button className="btn btn-ghost btn-sm" onClick={()=>openEdit(plo)}><PencilSquareIcon />Edit</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="pm-cards">
                {plos.map(plo => (
                  <div key={plo._id} className="pm-mcard">
                    <div className="pm-mc-top">
                      <span className="plo-code">{plo.code}</span>
                      <span className="pill" style={{background:`${DOMAIN_COLOR[plo.domain]}18`,color:DOMAIN_COLOR[plo.domain]}}>{plo.domain}</span>
                    </div>
                    <div className="pm-mc-desc">{plo.description}</div>
                    <div className="pm-mc-row">
                      <span className="pm-mc-label">Threshold</span>
                      <span style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:600,color:plo.threshold!=null?'#8b5cf6':'#c0c5dc'}}>
                        {plo.threshold!=null?`${plo.threshold}%`:'default'}
                      </span>
                    </div>
                    <div className="pm-mc-row">
                      <span className="pm-mc-label">CLOs Mapped</span>
                      <span className="clo-count-badge">
                        <ChartBarIcon style={{width:12,height:12,strokeWidth:2}}/>
                        {plo.cloCount} CLO{plo.cloCount!==1?'s':''}
                      </span>
                    </div>
                    <div className="pm-mc-actions">
                      <button className="btn btn-ghost btn-sm" onClick={()=>openEdit(plo)}><PencilSquareIcon />Edit</button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Map CLOs → PLOs ── */}
      {tab==='map' && (
        <>
          <div style={{background:'#f0f4ff',border:'1px solid #c7d2fe',borderRadius:12,padding:'10px 14px',marginBottom:'1.2rem',fontSize:'.78rem',color:'#3730a3'}}>
            🔗 Select a CLO's PLO from the dropdown. Each CLO maps to one PLO. Click <strong>Save Mappings</strong> when done.
          </div>
          <div className="map-filters">
            <div className="fl">
              <label className="fl-label">Class</label>
              <select className="fl-select" value={mapClass} onChange={e=>{setMapClass(e.target.value);setMapSubject('');}}>
                <option value="">All Classes</option>
                {sortByName(classes).map(c=><option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>
            <div className="fl">
              <label className="fl-label">Subject</label>
              {mapClass ? (
                <select className="fl-select" value={mapSubject} onChange={e=>setMapSubject(e.target.value)}>
                  <option value="">All Subjects</option>
                  {sortAsc(mapSubjects).map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              ) : (
                <input className="fl-input" placeholder="Type subject…" value={mapSubject} onChange={e=>setMapSubject(e.target.value)} />
              )}
            </div>
            <div className="fl">
              <label className="fl-label">Academic Year</label>
              <input className="fl-input" value={mapYear} onChange={e=>setMapYear(e.target.value)} placeholder="2025-2026" style={{minWidth:110}} />
            </div>
          </div>

          <div className="panel">
            <div className="panel-head">
              <div className="panel-title">CLOs ({clos.length}) — assign each to a PLO</div>
            </div>
            {cloLoading ? (
              <div className="pm-loading"><div className="spinner"/>Loading CLOs…</div>
            ) : clos.length === 0 ? (
              <div className="pm-empty">No CLOs found. Select filters or add CLOs in CLO Management first.</div>
            ) : clos.map(clo => (
              <div key={clo._id} className="clo-map-row">
                <div className="clo-map-info">
                  <span className="clo-map-code">{clo.code}</span>
                  <div className="clo-map-desc">{clo.description}</div>
                  <div className="clo-map-meta">{clo.class?.name} · {clo.subject} · {clo.academicYear}{clo.section?` · Sec ${clo.section}`:''}</div>
                </div>
                <select
                  style={{fontFamily:"'Sora',sans-serif",fontSize:'.8rem',padding:'7px 11px',border:`1.5px solid ${cloMappings[clo._id]?'#8b5cf6':'#e0e4f0'}`,borderRadius:9,background:cloMappings[clo._id]?'#f3f0ff':'#f8f9fc',color:cloMappings[clo._id]?'#8b5cf6':'#6b7194',outline:'none',minWidth:160,cursor:'pointer'}}
                  value={cloMappings[clo._id] || ''}
                  onChange={e => setCloMappings(p => ({ ...p, [clo._id]: e.target.value }))}>
                  <option value="">— No PLO —</option>
                  {plos.map(p => <option key={p._id} value={p._id}>{p.code} — {p.description.slice(0,40)}{p.description.length>40?'…':''}</option>)}
                </select>
              </div>
            ))}
          </div>

          {clos.length > 0 && (
            <div style={{display:'flex',justifyContent:'flex-end'}}>
              <button className="btn btn-green" onClick={handleSaveMappings} disabled={savingMap}>
                <CheckCircleIcon />{savingMap?'Saving…':'Save All Mappings'}
              </button>
            </div>
          )}
        </>
      )}

      {/* ── Add/Edit Modal ── */}
      {modal && createPortal(
        <div className="pm-modal-backdrop" onClick={()=>setModal(null)}>
          <div className="pm-modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">{modal==='add'?(readOnly?'Request New PLO':'Add PLO'):`Edit ${modal.code}`}</div>
              <button className="modal-close" onClick={()=>setModal(null)}><XMarkIcon /></button>
            </div>
            <div className="modal-grid">
              {modal==='add' && (
                <div className="mf full">
                  <label className="mf-label">PLO Code *</label>
                  <input className="mf-input" placeholder="e.g. PLO-1" value={form.code}
                    onChange={e=>setF('code',e.target.value)}
                    style={{fontFamily:"'JetBrains Mono',monospace"}} />
                  <span className="mf-hint">Must be unique for this school</span>
                </div>
              )}
              <div className="mf full">
                <label className="mf-label">Description *</label>
                <textarea className="mf-textarea" placeholder="Students will be able to…"
                  value={form.description} onChange={e=>setF('description',e.target.value)} />
              </div>
              <div className="mf">
                <label className="mf-label">Domain</label>
                <select className="mf-select" value={form.domain} onChange={e=>setF('domain',e.target.value)}>
                  {DOMAINS.map(d=><option key={d} value={d}>{d.charAt(0).toUpperCase()+d.slice(1)}</option>)}
                </select>
              </div>
              <div className="mf">
                <label className="mf-label">Custom Threshold % <span style={{fontWeight:400,color:'#b0b5cc',fontSize:'.64rem'}}>(blank = school default)</span></label>
                <input type="number" min={0} max={100} className="mf-input"
                  placeholder="e.g. 60" value={form.threshold}
                  onChange={e=>setF('threshold',e.target.value)}
                  style={{fontFamily:"'JetBrains Mono',monospace"}} />
              </div>
            </div>
            {readOnly && <div style={{background:'#fff7ed',border:'1px solid #fed7aa',borderRadius:8,padding:'8px 12px',fontSize:'.74rem',color:'#c2410c',marginTop:10}}>📋 This will be submitted as a request — admin must approve before it takes effect.</div>}
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
              <button className="btn btn-blue" onClick={handleSave} disabled={saving}>
                {saving?'Saving…':'Save PLO'}
              </button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}